import { Box, Card, CardContent, Typography, useTheme } from "@mui/material"
import { useContext, useState } from "react"
import { userContext } from "contexts/userContext"
import { getFeelingsForDate } from "scenes/YouthDailySummary/apiRequests"
import useApiRequestError from "utils/hooks/useApiRequestErrorHandling"
import { FeelingModel, SingleFeelingModel } from "services/openapi"
import FeelingDetails from "./FeelingDetails"
import useMountEffect from "utils/hooks/useMountEffect"
import { matchFeelingIdWithColor } from "utils/functions/matchFeelingIdWithColor"
import { toDateFormat } from "utils/functions/toDateFormat"
import ClientGroupId from "utils/clientGroupId"
import { useTranslation } from "react-i18next"
import FeelingDoughnut from "components/FeelingDoughnut"
import FeelingList from "scenes/MobileFeelingHistory/FeelingList"
import { getFeelingOptionsForClientGroup } from "utils/apiRequests/fetchFeelings"

const DailyFeelingSummary = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useContext(userContext)
  const { ErrorSnackbar, handleError } = useApiRequestError()
  const [feelings, setFeelings] = useState<SingleFeelingModel[]>([])
  const [feelingOptions, setFeelingOptions] = useState<FeelingModel[]>([])
  const [isLoading, setLoading] = useState<boolean>(true)

  useMountEffect(async function fetchData() {
    const today: string = toDateFormat(new Date())
    if (user?.id) {
      const userFeelings = await getFeelingsForDate(user.id, today, handleError)
      if (userFeelings) {
        setFeelings(userFeelings.feelings)
      }
    }
    await getFeelingOptionsForClientGroup(undefined, setLoading, setFeelingOptions, handleError)
  })

  let feelingSum: number = feelings.reduce((sum, feeling) => sum + (feeling.strength === 0 ? 1 : feeling.strength), 0)
  const chartData =
    user?.clientGroupId === ClientGroupId.ELDERLY
      ? Array(feelings.length).fill(1)
      : feelings.map((feeling) => (feeling.strength === 0 ? 1 : feeling.strength))

  const chartColors = feelings.map((feeling) => matchFeelingIdWithColor(feeling.feelingId, theme)!)
  const dataDoughnutLabels: string[] = feelings.map((feeling) => {
    const feelingNumber: number = feeling?.strength === 0 ? 1 : feeling.strength
    const feelingOption = feelingOptions.find((x) => x.id === feeling.feelingId)
    const feelingOptionName: string = t(`youth-view.feeling-selection.feeling-options.${feelingOption?.name}`) ?? ""
    const feelingPercentage: number = Math.round((feelingNumber / feelingSum) * 100)
    return `${feelingOptionName} (${feelingNumber}) (${feelingPercentage}%)`
  })

  const topUserFeelings = feelings
    .slice(0, 10)
    .map((feeling) => ({ feelingId: feeling.feelingId, sum: feeling.strength, total: 1 }))
  const hasTopTen: boolean = feelings?.length >= 10

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        width: theme.diaryView?.contentWidth,
        backgroundColor: theme.palette.primary.main,
        mb: 2,
        marginX: "auto",
      }}
    >
      {feelings.length ? (
        <CardContent>
          <ErrorSnackbar />
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center" }}>
            {!feelings.length && (
              <Typography variant="body1">{t("customer-mobile-view.feeling-history.no-feelings")}</Typography>
            )}
            <Box sx={{ flex: "1 1 auto", minWidth: 200, maxWidth: { xs: "60%", md: "80%" } }}>
              {isLoading ? (
                <></>
              ) : (
                <FeelingDoughnut
                  chartColors={chartColors}
                  chartData={chartData}
                  showPercentaces={false}
                  useFullSize={true}
                  chartLabels={dataDoughnutLabels}
                />
              )}
            </Box>
            <Box>
              {hasTopTen ? <Typography variant="h6">Top 10</Typography> : <></>}
              <FeelingList feelings={topUserFeelings} />
            </Box>
          </Box>
          <Box sx={{ pt: 2 }}>
            <FeelingDetails userFeelings={feelings} />
          </Box>
        </CardContent>
      ) : (
        <CardContent>
          <Box sx={{ mx: 7, my: 7 }}>{t("selection.no-feelings")}</Box>
        </CardContent>
      )}
    </Card>
  )
}

export default DailyFeelingSummary
